import {useHalState} from '@synako/haligator';
import {State} from '@synako/halitrek';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {isItemStateCatalogueFF} from 'ff-common';

export type EtatPromo = 'appliquee' | 'non_appliquee';

/**
 * État promotionnel d'une ligne de panier : `appliquee` si son prix est un prix promo,
 * `non_appliquee` si le catalogue affiche une promotion qui ne couvre pas la date de
 * livraison du panier, `undefined` s'il n'y a pas de promotion en jeu.
 *
 * Visible par tous les utilisateurs, contrairement au tag de catégorie de tarif de
 * `usePrixCategorieTag`, réservé aux commerciaux.
 *
 * Deux sources, toutes deux déjà chargées par les lignes de panier :
 * - `tarif_applique` de la ligne : catégorie du prix réellement appliqué. Fixée à l'ajout
 *   depuis le catalogue, puis corrigée par le contrôle Tolede à la date de livraison du panier.
 * - `tarif_applique` de l'item catalogue : catégorie au catalogue, calculée pour la prochaine
 *   livraison possible.
 *
 * `non_appliquee` compare donc la promotion visible aujourd'hui au catalogue au prix réellement
 * appliqué à la ligne : elle ne remonte pas une promotion déjà terminée au catalogue depuis
 * l'ajout au panier.
 *
 * Toujours `undefined` pour une ligne marketplace, qui n'a pas de catégorie de tarif, et pour
 * une ligne dont le commercial a saisi ou bloqué le prix.
 */
export function usePromoLignePanier(
  ligneDePanier: State<LigneDePanierDesc>,
): EtatPromo | undefined {
  const item = useHalState(ligneDePanier.follow('item_2'));
  const itemFF = isItemStateCatalogueFF(item.state) ? item.state : undefined;

  // Prix saisi ou bloqué par le commercial : ces lignes sont exclues des corrections du
  // contrôle Tolede, leur `tarif_applique` est donc figé et ne dit plus rien de fiable.
  if (ligneDePanier.data.prix_dero_souhaite != null) return undefined;

  // L'article est en promotion à la date du panier
  if (ligneDePanier.data.tarif_applique === 'P') return 'appliquee';
  if (itemFF?.data.tarif_applique === 'P') return 'non_appliquee';
  return undefined;
}
